import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiLink, FiBarChart2, FiSearch, FiArrowRight, FiCheckCircle, FiGrid, FiFileText, FiTarget, FiCamera, FiChevronDown, FiArrowUp, FiUser } from "react-icons/fi";
import { FaFacebookSquare, FaInstagram, FaYoutube, FaLinkedin, FaTwitterSquare } from "react-icons/fa";
import "./PlatformShowcase.css";

const tools = [
  { label: "Website Audit", icon: FiLink, path: "/seo-inspector", action: "Audit Now" },
  { label: "DA PA Checker", icon: FiBarChart2, path: "/da-pa-checker", action: "Check Now" },
  { label: "Keyword Research", icon: FiSearch, path: "/best-keyword-research-tool", action: "Find Keywords", keyword: true },
];

function DashboardPreview() {
  return (
    <div className="platform-dashboard-preview">
      <div className="platform-dashboard" role="img" aria-label="Illustrative SeoBix dashboard showing SEO score, traffic growth, backlinks and keywords. Example data.">
        <aside className="platform-sidebar" aria-hidden="true">
          <div className="platform-brand">SEØBIX</div>
          <div className="platform-sidebar-active"><FiGrid /> Dashboard</div>
          <small>SEO TOOLS</small>
          {[[FiTarget, "Website Audit"], [FiBarChart2, "DA PA Checker"], [FiSearch, "Keyword Research"], [FiFileText, "Rank Tracker"], [FiTarget, "Site Explorer"], [FiFileText, "SERP Checker"], [FiTarget, "Content Optimizer"]].map(([Icon, label]) => <div key={label}><Icon /> {label}</div>)}
          <small>SMO TOOLS</small>
          {[[FaFacebookSquare, "Facebook"], [FaInstagram, "Instagram"], [FaYoutube, "YouTube"], [FaLinkedin, "LinkedIn"], [FaTwitterSquare, "Twitter / X"]].map(([Icon, label]) => <div key={label} className={`platform-social platform-social-${label.split(" ")[0].toLowerCase()}`}><Icon /> {label}</div>)}
          <div className="platform-projects"><FiFileText /> Projects</div>
        </aside>
        <div className="platform-dashboard-main" aria-hidden="true">
          <div className="platform-dashboard-toolbar"><span>example.com <FiChevronDown /></span><b>Analyze</b><i><FiUser /></i></div>
          <div className="platform-metrics">
            {[["SEO Score", "92", "12%"], ["Organic Traffic", "125K", "28%"], ["Backlinks", "4.8K", "16%"], ["Keywords", "12K", "24%"]].map(([label, value, growth], index) => <div className={`platform-metric platform-tone-${index}`} key={label}><span>{label}</span><strong>{value}</strong><em><FiArrowUp /> {growth}</em></div>)}
          </div>
          <div className="platform-chart-heading"><strong>Organic Traffic</strong><span>Last 6 Months <FiChevronDown /></span></div>
          <div className="platform-chart">
            <svg viewBox="0 0 540 205" fill="none">
              <defs><linearGradient id="platform-chart-fill" x1="0" y1="0" x2="0" y2="1"><stop stopColor="#1584ff" stopOpacity=".2" /><stop offset="1" stopColor="#1584ff" stopOpacity=".01" /></linearGradient></defs>
              {[25, 65, 105, 145, 185].map((y, i) => <g key={y}><path d={`M48 ${y}H527`} stroke="#edf1f7" /><text x="4" y={y + 4} fill="#7d89a0" fontSize="11">{["200K", "150K", "100K", "50K", "0"][i]}</text></g>)}
              {[48, 144, 240, 336, 432, 527].map(x => <path key={x} d={`M${x} 25V185`} stroke="#edf1f7" />)}
              <path d="M48 175L125 150L201 119L287 133L372 98L451 89L527 62V185H48Z" fill="url(#platform-chart-fill)" />
              <path d="M48 175L125 150L201 119L287 133L372 98L451 89L527 62" stroke="#0879ff" strokeWidth="3" strokeLinejoin="round" />
              {[[48, 175], [125, 150], [201, 119], [287, 133], [372, 98], [451, 89], [527, 62]].map(([x, y]) => <circle key={x} cx={x} cy={y} r="5" fill="#0879ff" stroke="white" strokeWidth="2" />)}
              {["Jan", "Feb", "Mar", "Apr", "May", "Jun"].map((m, i) => <text key={m} x={48 + i * 91} y="203" fill="#7d89a0" fontSize="11">{m}</text>)}
            </svg>
            <span className="platform-growth"><FiArrowUp /> 128%</span>
          </div>
          <div className="platform-dashboard-features">{[[FiBarChart2, "Technical Issues"], [FiSearch, "Keyword Ideas"], [FiLink, "Backlink Analysis"], [FiCamera, "Site Health"]].map(([Icon, label], i) => <div key={label}><span className={`platform-feature-${i}`}><Icon /></span><b>{label}</b></div>)}</div>
          <span className="platform-example-label">Illustrative dashboard · Example data</span>
        </div>
      </div>
      <div className="platform-results-note" aria-hidden="true">
        <svg className="platform-results-rays" viewBox="0 0 50 55"><path d="M8 25L20 5M18 38L43 20M23 52L43 52" /></svg>
        <svg className="platform-results-arrow" viewBox="0 0 55 85"><path d="M29 77C50 49 38 21 12 9M12 9L17 24M12 9L29 11" /></svg>
        <span>See real<br />results!</span>
      </div>
    </div>
  );
}

export default function PlatformShowcase() {
  const [selected, setSelected] = useState(0);
  const [value, setValue] = useState("");
  const navigate = useNavigate();
  const tool = tools[selected];

  function submit(event) {
    event.preventDefault();
    navigate(`${tool.path}?${tool.keyword ? "keyword" : "url"}=${encodeURIComponent(value.trim())}`);
  }

  return (
    <section className="platform-showcase" aria-labelledby="platform-showcase-title">
      <div className="platform-showcase-inner">
        <div className="platform-copy">
          <p className="platform-eyebrow">ALL-IN-ONE SEO &amp; SMO PLATFORM</p>
          <h2 id="platform-showcase-title">Analyze. Optimize.<br /><span>Rank Higher.</span></h2>
          <p className="platform-description">Powerful SEO and social media tools to audit your website, track rankings, analyze competitors and grow your online presence — all in one place.</p>
          <div className="platform-toolbox">
            <div className="platform-tool-selector" role="group" aria-label="Choose an SEO tool">
              {tools.map(({ label, icon: Icon }, index) => <button type="button" key={label} aria-pressed={selected === index} onClick={() => { setSelected(index); if (tools[index].keyword !== tool.keyword) setValue(""); }}><Icon /> <span>{label}</span></button>)}
            </div>
            <form className="platform-form" onSubmit={submit}>
              <label className="platform-input"><FiLink aria-hidden="true" /><input required aria-label={tool.keyword ? "Keyword to research" : "Website URL"} placeholder={tool.keyword ? "Enter a keyword (e.g. digital marketing)" : "Enter your website URL (e.g. example.com)"} value={value} onChange={e => setValue(e.target.value)} /></label>
              <button type="submit">{tool.action} <FiArrowRight /></button>
            </form>
          </div>
          <ul className="platform-benefits">{["Accurate Data", "Beginner Friendly", "No Credit Card Required"].map(label => <li key={label}><FiCheckCircle />{label}</li>)}</ul>
          <div className="platform-note"><svg viewBox="0 0 85 65" aria-hidden="true"><path d="M80 49C25 80 5 43 18 8M6 19L18 7L24 25" /></svg><span>Get a complete SEO audit<br />in seconds!</span></div>
        </div>
        <DashboardPreview />
      </div>
    </section>
  );
}
